// services/api.ts
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { logger } from '../utils/logger';
import { isTokenExpired } from '../utils/tokenValidator';

const TOKEN_KEY = 'token';
const REFRESH_KEY = 'refreshToken';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// ---- REQUEST: adiciona Authorization e valida token ----
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_KEY);

    if (token) {
      // Verifica se o token expirou ANTES de fazer a requisição
      if (isTokenExpired(token)) {
        logger.warn('Token expirado detectado antes da requisição');
        // Remove tokens inválidos
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
        // Redireciona para login
        window.location.href = '/login';
        return Promise.reject(new Error('Token expirado'));
      }

      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ---- helpers internos ----
function logoutAndRedirect() {
  logger.info('Redirecionando para login - token inválido');
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  window.location.href = '/login';
}

async function refreshAccessToken() {
  const refresh = localStorage.getItem(REFRESH_KEY);
  if (!refresh) return null;

  // Usa axios “puro” pra não cair no mesmo interceptor
  const response = await axios.post(
    `${process.env.REACT_APP_API_URL}/token/refresh/`,
    { refresh }
  );

  const newAccess = response.data?.access;
  if (newAccess) {
    localStorage.setItem(TOKEN_KEY, newAccess);
    return newAccess;
  }
  return null;
}

let isRefreshing = false;
let queue: ((token: string | null) => void)[] = [];

// ---- RESPONSE: trata 401 / token_not_valid ----
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<any>) => {
    if (!error.response) {
      return Promise.reject(error);
    }

    const { status, data } = error.response;
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    const tokenInvalid =
      status === 401 &&
      (data?.code === 'token_not_valid' ||
        data?.detail === 'O token é inválido ou expirado');

    // Se não for erro de token, só repassa o erro
    if (!tokenInvalid) {
      return Promise.reject(error);
    }

    // Se já tentamos refazer essa requisição uma vez, desiste e manda pro login
    if (originalRequest._retry) {
      logoutAndRedirect();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    // Se já tem um refresh em andamento, coloca na fila pra usar o resultado
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        queue.push((newToken: string | null) => {
          if (!newToken) {
            reject(error);
            return;
          }
          originalRequest.headers = originalRequest.headers ?? {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          resolve(api(originalRequest));
        });
      });
    }

    isRefreshing = true;

    try {
      const newToken = await refreshAccessToken();

      // Processa fila de requisições pendentes
      queue.forEach((cb) => cb(newToken));
      queue = [];
      isRefreshing = false;

      if (!newToken) {
        // Refresh falhou → limpa tudo e manda pro login
        logoutAndRedirect();
        return Promise.reject(error);
      }

      // Atualiza header padrão
      api.defaults.headers.common.Authorization = `Bearer ${newToken}`;

      // Atualiza e refaz a requisição original
      originalRequest.headers = originalRequest.headers ?? {};
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (err) {
      // Falhou geral no refresh
      queue.forEach((cb) => cb(null));
      queue = [];
      isRefreshing = false;
      logoutAndRedirect();
      return Promise.reject(err);
    }
  }
);

export default api;
